import { hasIn } from "@aprao/common/src/object/hasIn.js";
import { times } from "@aprao/common/src/util/times.js";
import type { Promisable } from "type-fest";

import type {
  ApraoWorker,
  ApraoWorkerMessage,
  ProcessorType,
} from "./ApraoWorker.js";

/**
 * Just a wrapper to enable ApraoQueueMessage completion.
 */
const postMessage = (worker: Worker, message: ApraoWorkerMessage) =>
  worker.postMessage(message);

type GetProcessor<T> =
  T extends ApraoWorker<infer Processor> ? Processor : never;

export class EvictError extends Error {
  static EVICT_ERROR = "EvictError";

  static isEvictError = (error: unknown): error is EvictError =>
    hasIn(error, "name") && error.name === EvictError.EVICT_ERROR;

  override readonly name = EvictError.EVICT_ERROR;
}

/**
 * An executor service designed to work with an {@link ApraoWorker}.
 *
 * The concurrency (number of task than can run in parallel) and the queue size
 * (tasks that can wait to be run) can be tweaked but by default they are both
 * set to one. When the queue reaches maximum capacity, then the oldest pending
 * task is evicted.
 */
export class ApraoQueue<
  AWorker extends ApraoWorker,
  Processor extends ProcessorType = GetProcessor<AWorker>,
> {
  readonly _pendingList: Array<{
    run: (worker: Worker) => void;
    evict: () => void;
  }> = [];

  readonly _farm = new Set<Worker>();

  readonly _size: number;

  constructor(options: {
    workerFactory: () => Worker;
    errorHandler: (error: Error) => void;
    concurrency?: number;
    size?: number;
  }) {
    this._size = options.size ?? 1;

    times(options.concurrency ?? 1, () => {
      const worker = options.workerFactory();

      worker.addEventListener(
        "message",
        ({ data: message }: MessageEvent<ApraoWorkerMessage>) => {
          if (message.name === "ready") {
            this._farm.add(worker);
            this._triggerCycle();
          }
        },
        { once: true },
      );
      worker.addEventListener("error", (evt) => {
        options.errorHandler(
          new Error(
            `An error occurred in the worker\n\t${evt.message} in ${evt.filename}:${evt.lineno}.`,
          ),
        );
      });
      worker.addEventListener("messageerror", (evt) =>
        options.errorHandler(
          new Error(
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            `The worker received a message that can't be deserialized: ${evt}.`,
          ),
        ),
      );
    });
  }

  _triggerCycle = () => {
    for (const worker of this._farm) {
      const pending = this._pendingList.shift();
      if (pending != null) {
        // dispatch
        pending.run(worker);
      } else {
        // nothing to run
        break;
      }
    }
  };

  submit = async (option: { args: Parameters<Processor> }) =>
    await new Promise<Promisable<ReturnType<Processor>>>((resolve, reject) => {
      // Constrain the pending queue to the given size, replacing the head
      // (least recently submitted task) if necessary.
      if (this._pendingList.length === this._size) {
        this._pendingList.shift()?.evict();
      }
      this._pendingList.push({
        run: (worker: Worker) => {
          this._farm.delete(worker);

          worker.addEventListener(
            "message",
            ({ data: message }: MessageEvent<ApraoWorkerMessage>) => {
              if (message.name === "fulfilled") {
                resolve(message.payload as Promisable<ReturnType<Processor>>);
              } else if (message.name === "rejected") {
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                reject(message.payload);
              }

              this._farm.add(worker);
              this._triggerCycle();
            },
            { once: true },
          );

          postMessage(worker, { name: "run", payload: option.args });
        },
        evict: () => reject(new EvictError()),
      });

      this._triggerCycle();
    });
}
